"use client";

import cn from "classnames";

import { CompareRowLabel } from "@/components/compareRowLabel/compareRowLabel.component";
import { CompareTableIcon } from "@/components/compareTableSection/compareTableIcon.component";

import { portableTextToPlain } from "@/utils/portableText.util";

import { RichText } from "../richText/richText.component";
import { planCellToIconValue } from "./comparePlanTableCell";
import type { SanityPlanCompareRow } from "./useComparePlanTableSectionState";

export const ComparePlanTableRow = ({
  row,
  rowIndex,
  rowCount,
  gridTemplateColumns,
  mobileGridTemplateColumns,
}: ComparePlanTableRowProps) => {
  const isLastRow = rowIndex === rowCount - 1;
  const rowLabelText = portableTextToPlain(row.label);
  const renderRowLabel = () =>
    typeof row.label === "string" ? (
      row.label
    ) : (
      <RichText>{row.label}</RichText>
    );

  return (
    <>
      <div
        role="row"
        data-testid={`compare-plan-table-row-mobile-${row.id}`}
        className={cn("border-greyscale-strength border-b lg:hidden", {
          "border-b-0": isLastRow,
        })}>
        <div className="py-3 pr-4 pl-5" role="rowheader">
          <CompareRowLabel
            label={renderRowLabel()}
            tooltipLabel={rowLabelText}
            info={row.info}
            component="div"
          />
        </div>
        <div
          className="grid px-2 py-3"
          style={{ gridTemplateColumns: mobileGridTemplateColumns }}>
          {row.cells?.map((cell, cellIndex) => (
            <div
              role="cell"
              key={`${row.id}-mobile-${cellIndex}`}
              className="flex items-center justify-center">
              <CompareTableIcon value={planCellToIconValue(cell)} />
            </div>
          ))}
        </div>
      </div>

      <div
        role="row"
        data-testid={`compare-plan-table-row-desktop-${row.id}`}
        className={cn("border-greyscale-strength hidden border-b lg:grid", {
          "border-b-0": isLastRow,
        })}
        style={{ gridTemplateColumns }}>
        <div
          role="rowheader"
          className="h-[100px] w-[250px] max-w-[250px] px-4 py-8">
          <CompareRowLabel
            label={renderRowLabel()}
            tooltipLabel={rowLabelText}
            info={row.info}
            component="div"
          />
        </div>
        {row.cells?.map((cell, cellIndex) => (
          <div
            role="cell"
            key={`${row.id}-desktop-${cellIndex}`}
            className="flex h-[100px] items-center justify-center px-2 py-4">
            <CompareTableIcon value={planCellToIconValue(cell)} />
          </div>
        ))}
      </div>
    </>
  );
};

type ComparePlanTableRowProps = {
  row: SanityPlanCompareRow;
  rowIndex: number;
  rowCount: number;
  gridTemplateColumns: string;
  mobileGridTemplateColumns: string;
};
